const mortgageBankerSearchPage = (function mortgageBankerSearchPageModule() {
    let root;
    let formElement;
    let resultsElement;
    let inputElement;
    let clearButtonElement;
    let searchEndpoint;
    let activeRequest;
    let requestSequence = 0;

    function setupElements() {
        root = document.querySelector('.mortgage-banker-search-page');

        if (!root) {
            return false;
        }

        formElement = root.querySelector('[data-mortgage-banker-search-form]');
        resultsElement = root.querySelector('[data-mortgage-banker-results]');
        inputElement = formElement?.querySelector('input[name="query"]');
        clearButtonElement = formElement?.querySelector('[data-mortgage-banker-search-clear]');
        searchEndpoint = root.dataset.searchEndpoint || '/api/mortgage-bankers/search';

        return Boolean(formElement && resultsElement && inputElement);
    }

    function getPageSize() {
        const pageSizeElement = formElement.querySelector('input[name="pageSize"]');
        const pageSize = Number.parseInt(pageSizeElement?.value || '', 10);

        return Number.isInteger(pageSize) && pageSize > 0 ? pageSize : 10;
    }

    function updateFormState(page, pageSize) {
        const pageElement = formElement.querySelector('input[name="page"]');
        const pageSizeElement = formElement.querySelector('input[name="pageSize"]');

        if (pageElement) {
            pageElement.value = String(page);
        }

        if (pageSizeElement) {
            pageSizeElement.value = String(pageSize);
        }
    }

    function buildSearchUrl(page, pageSize) {
        const url = new URL(searchEndpoint, window.location.href);
        const parameters = new URLSearchParams({
            query: inputElement.value.trim(),
            page: String(page),
            pageSize: String(pageSize),
            pageUrl: root.dataset.pageUrl || window.location.pathname,
        });

        url.search = parameters.toString();
        return url;
    }

    function clearResults() {
        resultsElement.innerHTML = '';
        resultsElement.setAttribute('aria-busy', 'true');
    }

    function showError() {
        resultsElement.innerHTML = `
            <div class="mortgage-banker-search-page__results-inner">
                <p class="mortgage-banker-search-page__no-results">Unable to load mortgage bankers right now. Please try again.</p>
            </div>`;
    }

    function updateClearButton() {
        if (clearButtonElement) {
            clearButtonElement.hidden = inputElement.value.length === 0;
        }
    }

    function handleInput() {
        updateClearButton();
    }

    function handleClearClick() {
        inputElement.value = '';
        updateClearButton();
        inputElement.focus();
    }

    function scrollToResults() {
        const resultsTop = resultsElement.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
            top: Math.max(0, resultsTop),
            behavior: 'smooth',
        });
    }

    async function loadResults(
        page,
        pageSize,
        shouldScrollToResults = false,
        shouldFocusInput = false) {
        const requestId = ++requestSequence;
        const controller = new AbortController();

        if (activeRequest) {
            activeRequest.abort();
        }

        activeRequest = controller;
        clearResults();
        updateFormState(page, pageSize);

        try {
            const response = await fetch(buildSearchUrl(page, pageSize), {
                headers: {
                    Accept: 'text/html',
                    'X-Requested-With': 'XMLHttpRequest',
                },
                signal: controller.signal,
            });

            if (!response.ok) {
                throw new Error(`Mortgage banker search failed with status ${response.status}.`);
            }

            const html = await response.text();

            if (requestId === requestSequence) {
                resultsElement.innerHTML = html;

                if (shouldScrollToResults) {
                    window.requestAnimationFrame(scrollToResults);
                }
            }
        } catch (error) {
            if (error.name !== 'AbortError' && requestId === requestSequence) {
                showError();
            }
        } finally {
            if (requestId === requestSequence) {
                activeRequest = null;
                resultsElement.setAttribute('aria-busy', 'false');

                if (shouldFocusInput) {
                    inputElement.focus();
                }
            }
        }
    }

    function handleSearchSubmit(event) {
        event.preventDefault();
        loadResults(1, getPageSize(), false, true);
    }

    function handleResultClick(event) {
        if (!(event.target instanceof Element)) {
            return;
        }

        const link = event.target.closest('a');

        if (!link || !resultsElement.contains(link) || !link.closest('.mortgage-banker-search-page__pagination')) {
            return;
        }

        if (link.classList.contains('is-disabled')) {
            event.preventDefault();
            return;
        }

        const url = new URL(link.href, window.location.href);
        const page = Number.parseInt(url.searchParams.get('page') || '', 10);

        if (!Number.isInteger(page) || page < 1) {
            return;
        }

        event.preventDefault();

        const query = url.searchParams.get('query');
        const pageSize = Number.parseInt(url.searchParams.get('pageSize') || '', 10);

        if (query !== null) {
            inputElement.value = query;
            updateClearButton();
        }

        loadResults(
            page,
            Number.isInteger(pageSize) && pageSize > 0 ? pageSize : getPageSize(),
            true);
    }

    function handlePageSizeChange(event) {
        if (!(event.target instanceof HTMLSelectElement)
            || !event.target.matches('[data-mortgage-banker-page-size]')) {
            return;
        }

        const pageSize = Number.parseInt(event.target.value, 10);
        loadResults(1, Number.isInteger(pageSize) && pageSize > 0 ? pageSize : getPageSize());
    }

    function attachEvents() {
        formElement.addEventListener('submit', handleSearchSubmit);
        inputElement.addEventListener('input', handleInput);
        clearButtonElement?.addEventListener('click', handleClearClick);
        resultsElement.addEventListener('click', handleResultClick);
        resultsElement.addEventListener('change', handlePageSizeChange);
    }

    function init() {
        if (!setupElements()) {
            return;
        }

        updateClearButton();
        attachEvents();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();

export { mortgageBankerSearchPage };
